import {
  fromConsultationListData,
  fromConsultationListDataList,
  fromPatientCodesData,
  fromPatientConsultationData,
  fromPatientData,
  fromPatientRecordData,
  toConsultationUpdateRequest,
} from '@medprompt/shared';

async function fetchConsultationByPath(apiClient, consultationId, pathSuffix, options = {}) {
  if (!consultationId || typeof consultationId !== 'string') {
    throw new Error(`Invalid consultationId provided to fetch consultation ${pathSuffix}`);
  }

  const data = await apiClient.get(`/api/consultation/${consultationId}/${pathSuffix}`, options);

  if (!data || typeof data !== 'object' || !data.consultationId) {
    throw new Error('Invalid consultation data structure received');
  }

  if (data.consultationId !== consultationId) {
    throw new Error(`Consultation ID mismatch: requested ${consultationId}, received ${data.consultationId}`);
  }

  return data;
}

function asConsultationList(data) {
  return Array.isArray(data) ? fromConsultationListDataList(data) : [];
}

/**
 * ConsultationService - Handles consultation-related API operations for Scribe
 * Uses ApiClient for authenticated requests with proper queueing
 */
export class ConsultationService {
  constructor(apiClient) {
    this.apiClient = apiClient;
  }

  /**
   * Start a new consultation recording
   * @param {string} nhsId - Patient NHS ID
   * @param {string} templateId - Notes template ID (e.g., 'default')
   * @param {string} notesMode - Notes verbosity mode (BRIEF, MEDIUM, DETAILED)
   * @param {number} speakersCount - Expected speaker count for diarization (2-5)
   * @param {string} encounterType - EMIS consultation type (`FACE_TO_FACE` or `TELEPHONE`)
   * @returns {Promise<void>} Returns 204 on success, throws on 400 (invalid NHS ID)
   */
  async startRecording(nhsId, templateId, notesMode, speakersCount, encounterType) {
    const requestBody = {
      nhsId,
      speakersCount,
      templateId,
      notesMode,
      encounterType
    };
    return this.apiClient.post('/api/consultation/recording/start', requestBody);
  }

  /**
   * Upload a recording segment
   * @param {string} consultationId 
   * @param {number} segmentIndex - Zero-based segment index
   * @param {string} encodedData - Base64-encoded WAV audio data
   * @returns {Promise<void>}
   */
  async uploadSegment(consultationId, segmentIndex, encodedData) {
    const requestBody = {
      consultationId,
      segmentIndex,
      encodedData
    };
    return this.apiClient.post('/api/consultation/recording/segment/upload', requestBody);
  }

  /**
   * Get missing segment indices for a consultation
   * @param {string} consultationId
   * @param {number} [segmentsCount] - Expected total; required for a correct answer before complete
   * @returns {Promise<Array<number>>} Array of missing segment indices
   */
  async getMissingSegments(consultationId, segmentsCount) {
    const countQuery = typeof segmentsCount === 'number'
      ? `?segments_count=${encodeURIComponent(segmentsCount)}`
      : '';
    const data = await this.apiClient.get(
      `/api/consultation/${consultationId}/recording/missing_segments/fetch${countQuery}`
    );
    return Array.isArray(data) ? data : [];
  }

  /**
   * Complete a consultation recording
   * @param {string} consultationId 
   * @param {number} segmentsCount - Total number of segments
   * @returns {Promise<void>}
   */
  async completeRecording(consultationId, segmentsCount) {
    const requestBody = {
      consultationId,
      segmentsCount
    };
    return this.apiClient.post('/api/consultation/recording/complete', requestBody);
  }

  /**
   * Upload a pre-recorded consultation audio file
   * @param {string} nhsId - Patient NHS ID
   * @param {string} templateId - Notes template ID (e.g., 'default')
   * @param {string} notesMode - Notes verbosity mode (BRIEF, MEDIUM, DETAILED)
   * @param {number} speakersCount - Expected speaker count for diarization (2-5)
   * @param {string} encounterType - EMIS consultation type (`FACE_TO_FACE` or `TELEPHONE`)
   * @param {File} file - Audio file to upload
   * @returns {Promise<void>}
   */
  async uploadConsultationRecording(nhsId, templateId, notesMode, speakersCount, encounterType, file) {
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks
    const encodedChunks = [];
    
    // Read file in chunks and encode each
    for (let offset = 0; offset < file.size; offset += CHUNK_SIZE) {
      const chunk = file.slice(offset, offset + CHUNK_SIZE);
      const base64Chunk = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(chunk);
      });
      encodedChunks.push(base64Chunk);
    }

    const requestBody = {
      nhsId,
      speakersCount,
      templateId,
      notesMode,
      encounterType,
      fileName: file.name,
      encodedData: encodedChunks
    };

    return this.apiClient.post('/api/consultation/recording/upload', requestBody);
  }

  /**
   * Fetch data for a specific consultation
   * @param {string} consultationId 
   * @returns {Promise<Object>} Consultation data (PatientConsultationData)
   */
  async fetchConsultationData(consultationId, options = {}) {
    const data = await fetchConsultationByPath(this.apiClient, consultationId, 'data', options);
    return fromPatientConsultationData(data);
  }

  /**
   * Fetch list-row data for a specific consultation (scribe table patching)
   * @param {string} consultationId
   * @returns {Promise<Object>} Consultation list data
   */
  async fetchConsultationListData(consultationId, options = {}) {
    const data = await fetchConsultationByPath(this.apiClient, consultationId, 'list_data', options);
    return fromConsultationListData(data);
  }

  /**
   * Fetch audio file for a consultation
   * @param {string} consultationId 
   * @returns {Promise<Blob>} Audio file as blob (audio/mpeg)
   */
  async fetchConsultationAudio(consultationId) {
    return this.apiClient.getBlob(`/api/consultation/${consultationId}/file`);
  }

  /**
   * Fetch active consultations (not filed away)
   * Requires CONSULT_READ_ALL ability
   * @returns {Promise<Array>} Array of consultations
   */
  async fetchActiveConsultations(options = {}) {
    try {
      const data = await this.apiClient.get('/api/consultations/active/fetch', options);
      
      if (data === null || data === undefined) {
        console.error('fetchActiveConsultations received null/undefined data');
        return [];
      }
      
      return asConsultationList(data);
    } catch (error) {
      console.error('Error fetching active consultations:', error);
      throw error;
    }
  }

  /**
   * Fetch archived consultations (filed away)
   * @returns {Promise<Array>} Array of archived consultations
   */
  async fetchArchiveConsultations(options = {}) {
    try {
      const data = await this.apiClient.get('/api/consultations/archive/fetch', options);
      
      if (data === null || data === undefined) {
        console.error('fetchArchiveConsultations received null/undefined data');
        return [];
      }
      
      return asConsultationList(data);
    } catch (error) {
      console.error('Error fetching archive consultations:', error);
      throw error;
    }
  }

  /**
   * Fetch consultations assigned to the current user
   * @returns {Promise<Array>} Array of assigned consultations
   */
  async fetchAssignedConsultations(options = {}) {
    try {
      const data = await this.apiClient.get('/api/consultations/assigned/fetch', options);
      
      if (data === null || data === undefined) {
        console.error('fetchAssignedConsultations received null/undefined data');
        return [];
      }
      
      return asConsultationList(data);
    } catch (error) {
      console.error('Error fetching assigned consultations:', error);
      throw error;
    }
  }

  /**
   * Search consultations. Server requires CONSULT_READ_ALL (see ConsultationController).
   * Same request shape as DocumentService.searchDocuments (POST body `{ query }`).
   * @param {string} query - Search query
   * @returns {Promise<Array>} Matching consultations
   */
  async searchConsultations(query, options = {}) {
    try {
      const data = await this.apiClient.post('/api/consultations/search', { query }, options);
      if (data === null || data === undefined) {
        return [];
      }
      return asConsultationList(data);
    } catch (error) {
      console.error('Error searching consultations:', error);
      throw error;
    }
  }

  /**
   * Delete a consultation
   * Only allowed when status is READY, INVALID, or ABANDONED
   * @param {string} consultationId 
   * @returns {Promise<void>}
   */
  async deleteConsultation(consultationId) {
    return this.apiClient.post(`/api/consultation/${consultationId}/delete`);
  }

  /**
   * File away a consultation
   * @param {string} consultationId
   * @param {number|null} fileAwayDate - Optional file away date as UTC-midnight epoch millis (defaults to now on server)
   * @param {boolean} shouldIncludeTasks - Whether to include outcome tasks when filing away
   * @param {boolean} shouldIncludeComms - Whether to include communications when filing away
   * @returns {Promise<Object>} Response data
   */
  async fileAwayConsultation(consultationId, fileAwayDate = null, shouldIncludeTasks, shouldIncludeComms) {
    const requestBody = {
      shouldIncludeTasks: shouldIncludeTasks === true,
      shouldIncludeComms: shouldIncludeComms !== false
    };
    if (fileAwayDate != null) {
      requestBody.fileAwayDate = fileAwayDate;
    }
    return this.apiClient.post(`/api/consultation/${consultationId}/file_away`, requestBody);
  }

  /**
   * Fetch patient data for a specific consultation
   * @param {string} consultationId 
   * @returns {Promise<Object>} Patient data
   */
  async fetchPatientData(consultationId, options = {}) {
    const data = await this.apiClient.get(`/api/consultation/${consultationId}/patient/data`, options);
    return data == null ? null : fromPatientData(data);
  }

  /**
   * Fetch patient data for many consultations in one request.
   * @param {string[]} consultationIds
   * @returns {Promise<Object<string, Object|null>>} consultationId -> patient
   */
  async fetchPatientsData(consultationIds, options = {}) {
    const data = await this.apiClient.post('/api/consultations/patient/data', consultationIds, options);
    if (data == null) return data;
    const patients = {};
    Object.entries(data).forEach(([consultationId, patient]) => {
      patients[consultationId] = patient == null ? null : fromPatientData(patient);
    });
    return patients;
  }

  /**
   * Fetch patient record data for a consultation (patient, codes, document previews)
   * @param {string} consultationId 
   * @returns {Promise<Object>} Patient record data
   */
  async fetchPatientRecordData(consultationId, options = {}) {
    const data = await this.apiClient.get(`/api/consultation/${consultationId}/patient_record/data`, options);
    return data == null ? null : fromPatientRecordData(data);
  }

  /**
   * Fetch fresh patient problem codes from EMIS for a consultation (1-hour cache).
   * @param {string} consultationId
   * @returns {Promise<Object>} PatientCodesData
   */
  async fetchFreshPatientCodes(consultationId, options = {}) {
    const data = await this.apiClient.get(`/api/consultation/${consultationId}/patient_codes/fresh_data`, options);
    return data == null ? null : fromPatientCodesData(data);
  }

  /**
   * Update a consultation
   * @param {string} consultationId 
   * @param {Object} updateData 
   * @returns {Promise<Object>} Updated consultation data
   */
  async updateConsultation(consultationId, updateData) {
    return this.apiClient.post('/api/consultation/update', toConsultationUpdateRequest(consultationId, updateData));
  }

  /**
   * Regenerate all content for a consultation
   * @param {string} consultationId 
   * @returns {Promise<void>}
   */
  async regenerateConsultation(consultationId) {
    return this.apiClient.post(`/api/consultation/${consultationId}/regenerate`);
  }

  /**
   * Regenerate specific content type for a consultation
   * @param {string} consultationId 
   * @param {string} contentType - One of ConsultationContentType values
   * @returns {Promise<void>}
   */
  async regenerateConsultationContent(consultationId, contentType) {
    return this.apiClient.post(`/api/consultation/${consultationId}/regenerate?content_type=${contentType}`);
  }

  /**
   * Update a specific measurement in a consultation
   * @param {string} consultationId 
   * @param {Object} measurementUpdate - { measurementId, value?, unit?, comparator?, isSelected?, isDeselected? }
   * @returns {Promise<Object>}
   */
  async updateConsultationMeasurement(consultationId, measurementUpdate) {
    return this.updateConsultation(consultationId, { measurementUpdate });
  }

  /**
   * Add new measurements to a consultation
   * @param {string} consultationId 
   * @param {Array} measurements - Array of MeasurementRequest objects { type, value, unit, comparator }
   * @returns {Promise<Object>}
   */
  async addMeasurements(consultationId, measurements) {
    return this.updateConsultation(consultationId, { measurements });
  }

  /**
   * Add new outcome tasks to a consultation
   * @param {string} consultationId
   * @param {Array} outcomeTasks - Array of OutcomeTaskRequest objects { text, taskType, assigneeId, isUrgent }
   * @returns {Promise<Object>}
   */
  async addOutcomeTasks(consultationId, outcomeTasks) {
    return this.updateConsultation(consultationId, { outcomeTasks });
  }

  /**
   * Update a specific task in a consultation
   * @param {string} consultationId 
   * @param {Object} taskUpdate - { taskId, text?, isSelected?, isDeselected?, isUrgent?, isNotUrgent? }
   * @returns {Promise<Object>}
   */
  async updateConsultationTask(consultationId, taskUpdate) {
    return this.updateConsultation(consultationId, { taskUpdate });
  }

  /**
   * Update notes for a consultation
   * @param {string} consultationId 
   * @param {string} notes - Markdown notes content
   * @returns {Promise<Object>}
   */
  async updateConsultationNotes(consultationId, notes) {
    return this.updateConsultation(consultationId, { notes });
  }

  /**
   * Update existing code in a consultation
   * @param {string} consultationId 
   * @param {Object} existingCodeUpdate - { codeId, isSelected?, isDeselected?, significance?, description?, duration?, didChangeDuration? }
   * @returns {Promise<Object>}
   */
  async updateConsultationExistingCode(consultationId, existingCodeUpdate) {
    return this.updateConsultation(consultationId, { existingCodeUpdate });
  }

  /**
   * Update new/suggested code in a consultation
   * @param {string} consultationId 
   * @param {Object} newCodeUpdate - { codeId, isSelected?, isDeselected?, significance?, description?, duration?, didChangeDuration? }
   * @returns {Promise<Object>}
   */
  async updateConsultationNewCode(consultationId, newCodeUpdate) {
    return this.updateConsultation(consultationId, { newCodeUpdate });
  }

  /**
   * Delete existing codes from a consultation
   * @param {string} consultationId
   * @param {Array<string>} codeIds - ExistingCode codeId values to delete
   * @returns {Promise<Object>}
   */
  async deleteExistingCodes(consultationId, codeIds) {
    return this.updateConsultation(consultationId, { deletedExistingCodes: codeIds });
  }

  /**
   * Delete new/suggested codes from a consultation
   * @param {string} consultationId
   * @param {Array<string>} codeIds - NewCode codeId values to delete
   * @returns {Promise<Object>}
   */
  async deleteNewCodes(consultationId, codeIds) {
    return this.updateConsultation(consultationId, { deletedNewCodes: codeIds });
  }

  /**
   * Update NHS ID for a consultation
   * @param {string} consultationId 
   * @param {string} nhsId 
   * @returns {Promise<Object>}
   */
  async updateConsultationNhsId(consultationId, nhsId) {
    return this.updateConsultation(consultationId, { nhsId });
  }

  /**
   * Update consultation details (e.g. encounter type)
   * @param {string} consultationId
   * @param {Object} detailsUpdate - `{ encounterType }` (`FACE_TO_FACE` or `TELEPHONE`) and/or `{ isHiddenFromPatient }`
   * @returns {Promise<Object>}
   */
  async updateConsultationDetails(consultationId, detailsUpdate) {
    return this.updateConsultation(consultationId, { detailsUpdate });
  }

  /**
   * Search all codes in SNOMED database
   * @param {string} query - Search query
   * @returns {Promise<Array>} Search results (Code objects)
   */
  async searchAllCodes(query) {
    const encodedQuery = encodeURIComponent(query);
    return this.apiClient.get(`/api/codes/search?query=${encodedQuery}`);
  }

  /**
   * Search existing patient codes for a consultation
   * @param {string} consultationId 
   * @param {string} query - Search query
   * @returns {Promise<Object>} ExistingPatientCodes with activeCodes and significantPastCodes
   */
  async searchConsultationCodes(consultationId, query) {
    const encodedQuery = encodeURIComponent(query);
    return this.apiClient.get(`/api/consultation/${consultationId}/codes/search?query=${encodedQuery}`);
  }

  /**
   * Add existing patient codes to a consultation
   * @param {string} consultationId 
   * @param {Object} existingPatientCodes - { activeCodes: [], significantPastCodes: [] }
   * @returns {Promise<Object>}
   */
  async addExistingPatientCodes(consultationId, existingPatientCodes) {
    return this.updateConsultation(consultationId, { existingPatientCodes });
  }

  /**
   * Add new codes to a consultation
   * @param {string} consultationId 
   * @param {Array} newCodes - Array of Code objects to add
   * @returns {Promise<Object>}
   */
  async addNewPatientCodes(consultationId, newCodes) {
    return this.updateConsultation(consultationId, { newCodes });
  }

  /**
   * Fetch all notes templates
   * @returns {Promise<Array>} Array of Template objects
   */
  async fetchNotesTemplates() {
    return this.apiClient.get('/api/consultation/notes/templates/fetch');
  }

  /**
   * Create a new notes template
   * @param {Object} template - Template object with templateId (null for new), title, and content
   * @returns {Promise<void>}
   */
  async createNotesTemplate(template) {
    return this.apiClient.post('/api/consultation/notes/template/create', template);
  }

  /**
   * Update an existing notes template
   * @param {Object} templateUpdate - TemplateUpdate object with templateId, title, and content
   * @returns {Promise<void>}
   */
  async updateNotesTemplate(templateUpdate) {
    return this.apiClient.post('/api/consultation/notes/template/update', templateUpdate);
  }

  /**
   * Delete a notes template
   * @param {string} templateId - The ID of the template to delete
   * @returns {Promise<void>}
   */
  async deleteNotesTemplate(templateId) {
    return this.apiClient.post(`/api/consultation/notes/template/${templateId}/delete`);
  }

  /**
   * Generate a notes template using AI
   * @param {string} prompt - Instructions for what kind of template to generate
   * @param {string} notes - Existing template content to refine (optional)
   * @returns {Promise<Object>} GenerateResponse with content, message, and error fields
   */
  async generateNotesTemplate(prompt, existingTemplate) {
    const requestBody = { prompt, existingTemplate };
    return this.apiClient.post('/api/consultation/notes/template/generate', requestBody);
  }

  /**
   * Generate a referral letter using AI
   * @param {string} consultationId - The consultation to generate from
   * @param {string} prompt - Instructions for what kind of letter to generate / how to refine
   * @param {string|null} existingReferralLetter - Existing letter body to refine (optional)
   * @returns {Promise<Object>} GenerateResponse with content, message, and error fields
   */
  async generateReferralLetter(consultationId, prompt, existingReferralLetter) {
    const requestBody = {
      consultationId,
      prompt,
      existingReferralLetter: existingReferralLetter || null,
    };
    return this.apiClient.post('/api/consultation/referral/generate', requestBody);
  }
}
